import { Link, useParams } from "react-router-dom";
import { Logo } from "../../components/app/Logo";
import { EventTimeline } from "../../components/app/report/EventTimeline";
import { SummaryView } from "../../components/app/report/SummaryView";
import { RawView } from "../../components/app/report/RawView";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { formatDateRange } from "../../lib/baseline/format";
import { useSession } from "../../services/SessionContext";
import { visibleEventForShare } from "../../services/localRepository";
import type { ShareLinkType } from "../../types/domain";

export function PublicSharePage({ expectedType }: { expectedType: ShareLinkType }) {
  const { shareId } = useParams();
  const { repo } = useSession();
  const share = shareId ? repo.getPublicShare(shareId) : undefined;

  if (!share || share.link.type !== expectedType) {
    return (
      <div className="grid min-h-screen place-items-center bg-slate-50 px-4">
        <Card className="max-w-md">
          <CardContent className="p-6 text-center">
            <h1 className="text-xl font-bold text-navy-950">Share link unavailable</h1>
            <p className="mt-2 text-sm text-slate-600">This link is inactive, expired, or does not exist.</p>
            <Button asChild className="mt-5">
              <Link to="/">Go to BaselineBoard.app</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const events = share.events.map((event) => visibleEventForShare(event, share.link.hideSerialNumbers));
  const title = share.upload.projectName || share.upload.originalFileName;
  const showRaw = share.link.type === "SUPPORT";

  return (
    <div className="min-h-screen bg-slate-50 pb-8">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex h-16 max-w-5xl items-center justify-between px-4 sm:px-6">
          <Logo />
          <Badge className="bg-slate-50 text-slate-700 ring-slate-200">{share.link.type.replace("_", " ")}</Badge>
        </div>
      </header>
      <main className="mx-auto max-w-5xl space-y-5 px-4 py-5 sm:px-6">
        <Card>
          <CardContent className="p-5">
            <h1 className="text-2xl font-bold text-navy-950">{title}</h1>
            <p className="mt-1 text-sm text-slate-600">{share.upload.controllerName || "Unnamed controller"}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Badge className="bg-sky-50 text-sky-800 ring-sky-200">
                {formatDateRange(share.upload.dateRangeStart, share.upload.dateRangeEnd)}
              </Badge>
              <Badge className="bg-slate-50 text-slate-700 ring-slate-200">{share.upload.rowCount.toLocaleString()} events</Badge>
              <Badge className="bg-red-50 text-red-800 ring-red-200">{share.upload.criticalIssueCount + share.upload.warningCount} issues</Badge>
            </div>
          </CardContent>
        </Card>
        <SummaryView summary={{ ...share.summary, firstEvent: events[0], lastEvent: events[events.length - 1] }} />
        <section>
          <h2 className="mb-3 text-xl font-bold text-navy-950">Timeline</h2>
          <EventTimeline events={events} upload={share.upload} />
        </section>
        {showRaw ? (
          <section>
            <h2 className="mb-3 text-xl font-bold text-navy-950">Raw rows</h2>
            <RawView events={events} />
          </section>
        ) : null}
      </main>
    </div>
  );
}
